//garmentImageProcessor.ts — trims dead background and re-centres onto a square canvas

import { GARMENT_CANVAS } from '../../shared/appConfig'

const MARGIN_FRACTION = 0.06
//A product shot's background is near-uniform; anything this far off it is garment.
const DIFFERENCE_THRESHOLD = 18
//Clears a corner watermark (~7% of frame height) while leaving tapering garment edges intact.
const DENSITY_FRACTION = 0.15

interface Box {
	x: number
	y: number
	width: number
	height: number
}

//Store product images are often a garment floating in a wide frame — one catalogue image is
//1792x576 with the garment across ~26% of the width. Lucy reproduces what it sees, so the
//margin is wasted reference detail.
export async function normaliseGarmentImage(source: HTMLImageElement): Promise<Blob | null> {
	const box = contentBox(source) ?? {
		x: 0,
		y: 0,
		width: source.naturalWidth,
		height: source.naturalHeight,
	}
	return squareOnWhite(source, box)
}

//Projection-based rather than a plain bounding box. A raw bbox is defeated by any small
//artefact — a generator watermark in one corner alone stretched the box across the full
//frame. Counting content per row and per column and keeping the dense bands ignores specks.
function contentBox(image: HTMLImageElement): Box | null {
	const width = image.naturalWidth
	const height = image.naturalHeight
	if (width === 0 || height === 0) return null

	const canvas = document.createElement('canvas')
	canvas.width = width
	canvas.height = height
	const context = canvas.getContext('2d', { willReadFrequently: true })
	if (!context) return null
	context.drawImage(image, 0, 0)

	let pixels: Uint8ClampedArray
	try {
		pixels = context.getImageData(0, 0, width, height).data
	} catch {
		//A cross-origin image taints the canvas. The Worker proxies product images to make
		//them same-origin, so this only trips if that proxy is bypassed.
		return null
	}

	const background = backgroundColour(pixels, width, height)
	const columnCounts = new Int32Array(width)
	const rowCounts = new Int32Array(height)

	for (let y = 0; y < height; y += 1) {
		const row = y * width * 4
		for (let x = 0; x < width; x += 1) {
			const i = row + x * 4
			//Transparent pixels are background too, which covers PNG cut-outs.
			const isContent =
				pixels[i + 3] > 16 &&
				(Math.abs(pixels[i] - background[0]) > DIFFERENCE_THRESHOLD ||
					Math.abs(pixels[i + 1] - background[1]) > DIFFERENCE_THRESHOLD ||
					Math.abs(pixels[i + 2] - background[2]) > DIFFERENCE_THRESHOLD)
			if (isContent) {
				columnCounts[x] += 1
				rowCounts[y] += 1
			}
		}
	}

	const horizontal = denseRange(columnCounts)
	const vertical = denseRange(rowCounts)
	if (!horizontal || !vertical) return null

	return {
		x: horizontal.start,
		y: vertical.start,
		width: horizontal.end - horizontal.start,
		height: vertical.end - vertical.start,
	}
}

//Keeps the span between the first and last line carrying a meaningful share of the densest.
function denseRange(counts: Int32Array): { start: number; end: number } | null {
	let peak = 0
	for (const count of counts) if (count > peak) peak = count
	if (peak === 0) return null

	const threshold = Math.max(2, Math.floor(peak * DENSITY_FRACTION))
	let start = -1
	let end = -1
	for (let i = 0; i < counts.length; i += 1) {
		if (counts[i] >= threshold) {
			if (start === -1) start = i
			end = i
		}
	}
	return start === -1 ? null : { start, end: end + 1 }
}

function backgroundColour(
	pixels: Uint8ClampedArray,
	width: number,
	height: number,
): [number, number, number] {
	const corners = [
		0,
		(width - 1) * 4,
		(height - 1) * width * 4,
		((height - 1) * width + width - 1) * 4,
	]
	let r = 0
	let g = 0
	let b = 0
	for (const c of corners) {
		r += pixels[c]
		g += pixels[c + 1]
		b += pixels[c + 2]
	}
	const n = corners.length
	return [Math.round(r / n), Math.round(g / n), Math.round(b / n)]
}

//White canvas, not transparent: Decart asks for garments on plain white.
function squareOnWhite(image: HTMLImageElement, box: Box): Promise<Blob | null> {
	const side = GARMENT_CANVAS
	const canvas = document.createElement('canvas')
	canvas.width = side
	canvas.height = side
	const context = canvas.getContext('2d', { alpha: false })
	if (!context) return Promise.resolve(null)

	context.fillStyle = '#ffffff'
	context.fillRect(0, 0, side, side)

	const margin = side * MARGIN_FRACTION
	const available = side - margin * 2
	const scale = Math.min(available / box.width, available / box.height)
	const drawWidth = box.width * scale
	const drawHeight = box.height * scale
	context.imageSmoothingQuality = 'high'
	context.drawImage(
		image,
		box.x,
		box.y,
		box.width,
		box.height,
		(side - drawWidth) / 2,
		(side - drawHeight) / 2,
		drawWidth,
		drawHeight,
	)

	return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
}
